import { Skeleton, Tabs } from 'antd'
import { TenantTabAccessPlan } from 'components/tenants/tenant-tab-access-plan'
import TenantTabActivityTracking from 'components/tenants/tenant-tab-activity-tracking'
import TenantTabIncidences from 'components/tenants/tenant-tab-incidences'
import { TenantTabPartnerIntegration } from 'components/tenants/tenant-tab-partner-integration'
import { TenantTabTagging } from 'components/tenants/tenant-tab-tagging'
import { TenantTabWaitList } from 'components/tenants/tenant-tab-waitlist'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useAppState } from 'state'
import { plusSVG } from '../../assets/svg/plus'
import { Box } from '../../atom/box'
import { ButtonModal } from '../../atom/button'
import { TabsWrapper } from '../../atom/chargers'
import { BackgroundContainer } from '../../atom/layout/backgroundContainer'
import { TabsButtonWrapper } from '../../atom/users-page-redesign-styles'
import { AlertError } from '../../components/error'
import { withAuthenticatedLayout } from '../../components/layouts/layout'
import { TenantTabChargers } from '../../components/tenants/tenant-tab-chargers'
import { TenantDeploymentTab } from '../../components/tenants/tenant-tab-deployment'
import { TenantTabLocation } from '../../components/tenants/tenant-tab-location'
import { TenantTabOverview } from '../../components/tenants/tenant-tab-overview'
import { TenantTabPricingSchema } from '../../components/tenants/tenant-tab-pricing-schema'
import { useTenantTranslation } from '../../hooks/translation/useTenantTranslation'
import { usePricingSchemas } from '../../hooks/usePricingSchemas'
import { SwtchError } from '../../models/error'
import { Tenant, TenantPayload } from '../../models/tenant'
import { GetTenant, UpdateTenant } from '../../services/data-provider/tenants'
import { isManagerToTenants } from 'models/user'

const TenantBasePage: React.FC = () => {
  document.querySelector('body')?.classList.add('redesignActive')
  // @ts-ignore
  const { tenantId } = useParams()
  const queryParams = new URLSearchParams(window.location.search)

  const defaultTab = queryParams.get('defaultTab') || 'overview'
  const [tab, setTab] = useState(defaultTab)
  const { currentUser, isMockUpEnabled } = useAppState()
  const isAdmin = currentUser?.role === 'admin'
  const isManagerOperator = isAdmin || (currentUser?.role === 'user' && isManagerToTenants(currentUser?.accesses || []))
  const [tenant, setTenant] = useState<Tenant>()
  const [tenantPayload, setTenantPayload] = useState<TenantPayload>()
  const [loading, setLoading] = useState<boolean>()
  const [error, setError] = useState<SwtchError>()
  const [showCreateDiscountSchemaModal, setShowCreateDiscountSchemaModal] = useState(false)
  const { showCreatePricingSchemaModal, setShowCreatePricingSchemaModal } = usePricingSchemas(tenant)
  const { discountPlanText } = useGeneralTranslation()
  const {
    tenantTabChargerText,
    overviewText,
    locationText,
    pricingPlanText,
    deploymentText,
    partnerIntegrationText,
    taggingText,
    incidencesText,
    accessPlanText,
  } = useTenantTranslation()

  useEffect(() => {
    setLoading(true)
    GetTenant(tenantId, isMockUpEnabled)
      .then((tenant) => setTenant(tenant))
      .catch((err: SwtchError) => setError(err))
      .finally(() => setLoading(false))
  }, [tenantId])

  useEffect(() => {
    if (tenantPayload && !loading) {
      setLoading(true)
      UpdateTenant(tenantPayload)
        .then(setTenant)
        .catch((err: SwtchError) => setError(err))
        .finally(() => setLoading(false))
    }
  }, [tenantPayload])

  const handleTabOptions = () => {
    switch (tab) {
      case 'pricingSchema':
        return (
          <>
            <TabsButtonWrapper className="discount-plans-button">
              <ButtonModal
                size="large"
                click={() => setShowCreateDiscountSchemaModal(true)}
                icon={plusSVG}
                text={discountPlanText}
                type="primary"
              />
            </TabsButtonWrapper>
            <TabsButtonWrapper className="plans-button">
              <ButtonModal
                size="large"
                click={() => setShowCreatePricingSchemaModal(true)}
                icon={plusSVG}
                text={pricingPlanText}
                type="primary"
              />
            </TabsButtonWrapper>
          </>
        )
      default:
        return null
    }
  }

  if (!tenant) {
    return (
      <Box>
        <Skeleton active />
      </Box>
    )
  }
  return (
    <BackgroundContainer>
      <AlertError error={error} />
      <TabsWrapper>
        <Tabs activeKey={tab} tabBarExtraContent={handleTabOptions()} onChange={(key) => setTab(key)}>
          {!tenant || (
            <Tabs.TabPane key="overview" tab={overviewText}>
              <TenantTabOverview tenant={tenant} onSave={setTenantPayload} />
            </Tabs.TabPane>
          )}
          {!tenant || (
            <Tabs.TabPane key="location" tab={locationText}>
              <TenantTabLocation tenant={tenant} onSave={setTenantPayload} />
            </Tabs.TabPane>
          )}
          {!tenant || (
            <Tabs.TabPane key="accessPlan" tab={accessPlanText}>
              <TenantTabAccessPlan tenant={tenant} />
            </Tabs.TabPane>
          )}
          {!tenant || (
            <Tabs.TabPane key="waitlist" tab={'Waitlist'}>
              <TenantTabWaitList tenant={tenant} onSave={() => {}} />
            </Tabs.TabPane>
          )}
          {isAdmin && (
            <>
              {!tenant || (
                <Tabs.TabPane key="deployment" tab={deploymentText}>
                  <TenantDeploymentTab tenant={tenant} />
                </Tabs.TabPane>
              )}
              {!tenant?.chargerCount || (
                <Tabs.TabPane key="chargers" tab={`${tenantTabChargerText} (${tenant.chargerCount})`}>
                  <TenantTabChargers />
                </Tabs.TabPane>
              )}
              <Tabs.TabPane key="pricingSchema" tab={pricingPlanText}>
                <TenantTabPricingSchema
                  tenant={tenant}
                  showCreateDiscountSchemaModal={showCreateDiscountSchemaModal}
                  showCreatePricingSchemaModal={showCreatePricingSchemaModal}
                  setShowCreatePricingSchemaModal={setShowCreatePricingSchemaModal}
                  setShowCreateDiscountSchemaModal={setShowCreateDiscountSchemaModal}
                />
              </Tabs.TabPane>
              {!tenant || (
                <Tabs.TabPane key="partnerIntegration" tab={partnerIntegrationText}>
                  <TenantTabPartnerIntegration tenant={tenant} onSave={setTenantPayload} />
                </Tabs.TabPane>
              )}
              {!tenant || (
                <Tabs.TabPane key="activityTraceability" tab="Activity Tracking">
                  <TenantTabActivityTracking tenant={tenant} />
                </Tabs.TabPane>
              )}
            </>
          )}
          {/* For google site */}
          {(isAdmin || (isManagerOperator && tenant.name === '6420-Sequence-Dr-San-Diego-CA')) && (
            <>
              {!tenant || (
                <Tabs.TabPane key="incidences" tab={incidencesText}>
                  <TenantTabIncidences tenant={tenant} />
                </Tabs.TabPane>
              )}
            </>
          )}
          {isAdmin && (
            <>
              {!tenant || (
                <Tabs.TabPane key="tagging" tab={taggingText}>
                  <TenantTabTagging tenant={tenant} />
                </Tabs.TabPane>
              )}
            </>
          )}
        </Tabs>
      </TabsWrapper>
    </BackgroundContainer>
  )
}

export const TenantPage = withAuthenticatedLayout(TenantBasePage)
